import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, LayoutGrid, List, UserPlus } from "lucide-react";
import { api, initials, avatarColor, STATUS_META, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Donut } from "@/components/Donut";
import EmployeeForm from "@/components/EmployeeForm";
import { toast } from "sonner";

const ALL = "__all__";

export default function People() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [regions, setRegions] = useState([]);
  const [rcsms, setRcsms] = useState([]);
  const [cnats, setCnats] = useState([]);
  const [filters, setFilters] = useState({ search: "", region_id: ALL, job_role: ALL, rcsm_id: ALL, cnat_id: ALL, status: ALL });
  const [view, setView] = useState("grid");
  const [formOpen, setFormOpen] = useState(false);

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    api.get("/employees", { params }).then((r) => setEmployees(r.data));
  };

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data));
    api.get("/users", { params: { role: "rcsm" } }).then((r) => setRcsms(r.data));
    api.get("/users", { params: { role: "cnat" } }).then((r) => setCnats(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const jobRoles = useMemo(() => [...new Set(employees.map((e) => e.job_role).filter(Boolean))], [employees]);
  const canAdd = ["admin", "hr", "rcsm", "cnat"].includes(user.role);

  const assignMe = async (emp) => {
    try {
      await api.post(`/employees/${emp.id}/assign-me`);
      toast.success(`${emp.full_name} assigned to you`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">People</h1>
          <p className="mt-1 text-sm text-slate-500">Search and manage employees across the organisation.</p>
        </div>
        {canAdd && (
          <Button data-testid="add-employee-button" onClick={() => setFormOpen(true)} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="mr-1.5 h-4 w-4" /> Add Employee
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="people-search-input"
            placeholder="Search by name…"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          <FilterSelect testid="region-filter-select" value={filters.region_id} onChange={(v) => setFilters((f) => ({ ...f, region_id: v }))} placeholder="Region" options={regions.map((r) => ({ v: r.id, l: r.name }))} />
          <FilterSelect testid="role-filter-select" value={filters.job_role} onChange={(v) => setFilters((f) => ({ ...f, job_role: v }))} placeholder="Role" options={jobRoles.map((r) => ({ v: r, l: r }))} />
          <FilterSelect testid="rcsm-filter-select" value={filters.rcsm_id} onChange={(v) => setFilters((f) => ({ ...f, rcsm_id: v }))} placeholder="RCSM" options={rcsms.map((r) => ({ v: r.id, l: r.name }))} />
          <FilterSelect testid="cnat-filter-select" value={filters.cnat_id} onChange={(v) => setFilters((f) => ({ ...f, cnat_id: v }))} placeholder="CNAT" options={cnats.map((r) => ({ v: r.id, l: r.name }))} />
          <FilterSelect testid="status-filter-select" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} placeholder="Status" options={Object.entries(STATUS_META).map(([k, m]) => ({ v: k, l: m.label }))} />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-slate-500">{employees.length} employee{employees.length !== 1 ? "s" : ""}</p>
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-0.5">
            <button data-testid="view-grid" onClick={() => setView("grid")} className={`rounded p-1.5 ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button data-testid="view-table" onClick={() => setView("table")} className={`rounded p-1.5 ${view === "table" ? "bg-slate-900 text-white" : "text-slate-500"}`}><List className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {employees.length === 0 && <p className="py-10 text-center text-slate-400">No employees match your filters.</p>}

      {view === "grid" ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {employees.map((e) => (
            <div
              key={e.id}
              data-testid="employee-card"
              onClick={() => navigate(`/people/${e.id}`)}
              className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold ${avatarColor(e.full_name)}`}>
                  {initials(e.full_name)}
                </div>
                <Donut value={e.elearning_percentage} size={48} stroke={6} />
              </div>
              <p className="mt-3 font-heading font-bold text-slate-900">{e.full_name}</p>
              <p className="text-sm text-slate-500">{e.job_role}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {e.region_name && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{e.region_name}</span>}
                <StatusBadge status={e.employment_status} />
              </div>
              {!e.can_manage && (user.role === "rcsm" || user.role === "cnat") && (
                <button
                  data-testid="assign-me-button"
                  onClick={(ev) => { ev.stopPropagation(); assignMe(e); }}
                  className="mt-3 flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Assign to me
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table data-testid="employee-table" className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">RCSM</th>
                <th className="px-4 py-3">Training</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id} data-testid="employee-row" onClick={() => navigate(`/people/${e.id}`)} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{e.full_name}</td>
                  <td className="px-4 py-3 text-slate-600">{e.job_role}</td>
                  <td className="px-4 py-3 text-slate-600">{e.region_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{e.rcsm_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{e.elearning_percentage}%</td>
                  <td className="px-4 py-3"><StatusBadge status={e.employment_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EmployeeForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </div>
  );
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {placeholder}s</SelectItem>
        {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.other;
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>;
}
